import React, { useCallback, useEffect, useState } from 'react';
import type { Medication } from '@components/Patient/MedicationsDataBlock';
import MedicationTimelineBlock from '@components/Patient/MedicationTimelineBlock';
import { useAccessToken } from '@features/user';
import { Alert, Box, Typography } from '@mui/material';

import APIService from '../../../services/APIService';

import CollapsibleBaseBlock from './CollapsibleBaseBlock';

interface PatientModuleData {
  medications_block?: {
    current_data?: {
      medications?: Medication[];
    };
  };
  medications_ptmodule?: {
    current_data?: {
      medications?: Medication[];
    };
  };
}

interface PatientDataResponse {
  patient_data?: {
    patient_module_data?: PatientModuleData;
  };
}

interface MedicationTimelineEncounterBlockProps {
  patientId?: string | null;
  defaultCollapsed?: boolean;
  onCollapseChange?: (isCollapsed: boolean) => void;
}

const MedicationTimelineEncounterBlock: React.FC<MedicationTimelineEncounterBlockProps> = ({
  patientId,
  defaultCollapsed = true,
  onCollapseChange,
}) => {
  const { getAccessToken } = useAccessToken();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [hasBeenOpened, setHasBeenOpened] = useState(!defaultCollapsed);
  const [medications, setMedications] = useState<Medication[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setIsCollapsed(defaultCollapsed);
    setHasBeenOpened(!defaultCollapsed);
  }, [patientId, defaultCollapsed]);

  const handleCollapseChange = useCallback(
    (collapsed: boolean) => {
      setIsCollapsed(collapsed);
      if (!collapsed) {
        setHasBeenOpened(true);
      }
      if (onCollapseChange) {
        onCollapseChange(collapsed);
      }
    },
    [setIsCollapsed, onCollapseChange],
  );

  const fetchMedications = useCallback(async () => {
    if (!patientId || (isCollapsed && !hasBeenOpened)) {
      setMedications(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIGetRequest({
        requestString: `/patient/getPatientData?patient_id=${patientId}`,
        accessToken,
      });

      if (!response.ok) {
        throw new Error('Unable to load patient data');
      }

      const data = (await response.value) as PatientDataResponse | undefined;
      const patientModuleData = data?.patient_data?.patient_module_data;

      // Extract medications from the medications_block module
      const medicationsModule =
        patientModuleData?.medications_block || patientModuleData?.medications_ptmodule;
      const medicationsData = medicationsModule?.current_data?.medications || [];

      // Medications from the module should already be in the correct format
      setMedications(medicationsData);
    } catch (err) {
      console.error('[MedicationTimelineEncounterBlock] Failed to fetch medications', err);
      setError(err as Error);
      setMedications([]);
    } finally {
      setIsLoading(false);
    }
  }, [patientId, isCollapsed, hasBeenOpened, getAccessToken]);

  useEffect(() => {
    void fetchMedications();
  }, [fetchMedications]);

  const renderContent = () => {
    if (!patientId) {
      return (
        <Typography variant="body2" color="text.secondary">
          Select a patient to view their medication history.
        </Typography>
      );
    }

    return (
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to load medications. Please try again later.
          </Alert>
        )}
        <MedicationTimelineBlock
          medications={medications ?? undefined}
          isLoading={isLoading}
          hideHeader
          emptyMessage="No historical medications to display."
        />
      </Box>
    );
  };

  return (
    <CollapsibleBaseBlock
      defaultCollapsed={defaultCollapsed}
      onCollapseChange={handleCollapseChange}
    >
      <CollapsibleBaseBlock.Header
        title="Medication Timeline"
        subtitle={patientId ? null : 'Patient required to load medications'}
      />
      <CollapsibleBaseBlock.Body>{renderContent()}</CollapsibleBaseBlock.Body>
    </CollapsibleBaseBlock>
  );
};

export default MedicationTimelineEncounterBlock;
